import clsx from 'clsx';
import type { ReactNode } from 'react';

export type Tone = 'neutral' | 'success' | 'warning' | 'danger' | 'info' | 'purple' | 'orange';

// Every tone's dark fill must NOT resolve to `--surface-raised` dark
// (#292524 / stone-800) — Badge's primary host is a Card, and a chip whose
// background matches its own card's background renders as invisible
// floating text. `neutral` shipped exactly that bug (`dark:bg-stone-800`,
// byte-identical to surfaceRaised dark, 1.00:1) before Badge had any
// consumers — moved to `stone-700` (#44403c, 1.48:1 against the card) so
// the fix lands once here instead of per-consumer. `purple`/`orange` follow
// the same `bg-X-100 text-X-900 dark:bg-X-900 dark:text-X-100` shape as
// success/warning/danger/info. Every tone's dark bg-vs-card ratio and every
// tone's own text/bg AA ratio are asserted in src/test/contrast.test.ts, not
// just hand-verified.
const TONES: Record<Tone, string> = {
  neutral: 'bg-stone-100 text-stone-700 dark:bg-stone-700 dark:text-stone-300',
  success: 'bg-teal-100 text-teal-900 dark:bg-teal-900 dark:text-teal-100',
  warning: 'bg-amber-100 text-amber-900 dark:bg-amber-900 dark:text-amber-100',
  danger: 'bg-red-100 text-red-900 dark:bg-red-900 dark:text-red-100',
  info: 'bg-sky-100 text-sky-900 dark:bg-sky-900 dark:text-sky-100',
  purple: 'bg-purple-100 text-purple-900 dark:bg-purple-900 dark:text-purple-100',
  orange: 'bg-orange-100 text-orange-900 dark:bg-orange-900 dark:text-orange-100',
};

// `wrap` is for sentence-length badges in a constrained column: nowrap there
// can neither wrap nor shrink, so it becomes the column's min-content floor or
// runs under a neighbour (CLAUDE.md §7.1). It swaps the class rather than
// letting a caller add `whitespace-normal`, because clsx keeps both and the
// winner is then decided by CSS order, not by the caller.
export function Badge({
  tone = 'neutral',
  wrap = false,
  className,
  title,
  children,
}: {
  tone?: Tone;
  wrap?: boolean;
  className?: string;
  title?: string;
  children: ReactNode;
}) {
  return (
    <span
      title={title}
      className={clsx(
        'inline-flex items-center rounded-full px-2.5 py-1 text-xs font-semibold',
        wrap ? 'whitespace-normal' : 'whitespace-nowrap',
        TONES[tone],
        className,
      )}
    >
      {children}
    </span>
  );
}
